(() => {
  const FILTER_PARAM = 'biscuits_category';

  class BiscuitsCollectionFilters {
    constructor(element) {
      this.element = element;
      this.rootUrl = element.dataset.biscuitsFilterRootUrl;
      this.sectionId = element.dataset.biscuitsFilterSectionId;
      this.gridContainer = document.getElementById('ProductGridContainer');
      this.abortController = null;

      if (!this.rootUrl || !this.sectionId || !this.gridContainer) return;

      this.element.addEventListener('click', this.onChipClick.bind(this));
      this.gridContainer.addEventListener('click', this.onPaginationClick.bind(this));
      window.addEventListener('popstate', () => this.loadFromCurrentUrl());
      this.loadFromCurrentUrl();
    }

    getChips() {
      return Array.from(this.element.querySelectorAll('[data-biscuits-filter-chip]'));
    }

    getSelectedChip() {
      const selectedHandle = new URL(window.location.href).searchParams.get(FILTER_PARAM) || '';
      return this.getChips().find((chip) => chip.dataset.biscuitsFilterHandle === selectedHandle) || this.getChips()[0];
    }

    setActiveChip(activeChip) {
      this.getChips().forEach((chip) => {
        const isActive = chip === activeChip;
        chip.classList.toggle('biscuits-filter-chip--active', isActive);

        if (isActive) {
          chip.setAttribute('aria-current', 'page');
        } else {
          chip.removeAttribute('aria-current');
        }
      });
    }

    onChipClick(event) {
      const chip = event.target.closest('[data-biscuits-filter-chip]');
      if (!chip || !this.element.contains(chip)) return;

      event.preventDefault();
      const nextUrl = new URL(chip.href, window.location.origin);

      if (nextUrl.href !== window.location.href) {
        window.history.pushState({}, '', nextUrl);
      }

      this.loadFromCurrentUrl();
    }

    onPaginationClick(event) {
      const paginationLink = event.target.closest('.pagination a');
      if (!paginationLink) return;

      event.preventDefault();
      const paginationUrl = new URL(paginationLink.href, window.location.origin);
      const currentUrl = new URL(window.location.href);

      // Preserve the filter parameter when paginating
      const filterValue = currentUrl.searchParams.get(FILTER_PARAM) || '';
      if (filterValue) {
        paginationUrl.searchParams.set(FILTER_PARAM, filterValue);
      } else {
        paginationUrl.searchParams.delete(FILTER_PARAM);
      }

      window.history.pushState({}, '', paginationUrl);
      this.loadFromCurrentUrl();
    }

    async loadFromCurrentUrl() {
      const activeChip = this.getSelectedChip();
      if (!activeChip) return;

      this.setActiveChip(activeChip);
      this.element.setAttribute('aria-busy', 'true');
      this.gridContainer.setAttribute('aria-busy', 'true');

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const sourceUrl = new URL(activeChip.dataset.biscuitsFilterSourceUrl, window.location.origin);
        sourceUrl.searchParams.set('section_id', this.sectionId);

        // Preserve the page parameter from the current URL
        const currentUrl = new URL(window.location.href);
        const pageParam = currentUrl.searchParams.get('page');
        if (pageParam) {
          sourceUrl.searchParams.set('page', pageParam);
        }

        const response = await fetch(sourceUrl.toString(), {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Unable to load collection: ${response.status}`);

        const responseHtml = await response.text();
        const sourceGrid = new DOMParser()
          .parseFromString(responseHtml, 'text/html')
          .getElementById('ProductGridContainer');

        if (!sourceGrid) throw new Error('The selected collection did not return a product grid.');

        this.gridContainer.innerHTML = sourceGrid.innerHTML;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Biscuits collection filter could not load products.', error);
        }
      } finally {
        this.element.removeAttribute('aria-busy');
        this.gridContainer.removeAttribute('aria-busy');
      }
    }
  }

  const initialise = () => {
    document.querySelectorAll('[data-biscuits-filter-chips]').forEach((element) => {
      if (!element.dataset.biscuitsFilterInitialised) {
        element.dataset.biscuitsFilterInitialised = 'true';
        new BiscuitsCollectionFilters(element);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }
})();
